
import { MonetizationType, ObjectType, PresentationType, Scoring } from '@/@types/graphql-types';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
} from '@/components/buybox-new/graphql/GetBuyBoxOffers.query';
import { timestamp } from '@/helpers/date-helper';
import { OfferPresentationType } from '@/interfaces/titles';
import { BuyBoxFilterOptions } from '@/components/experiments/buybox-exp-4/utils';
import { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const BuyBoxCountrySwitcher = () => import('@/components/buybox-new/BuyBoxCountrySwitcher.vue');
const PromotedBuyBoxNew = () => import('@/components/buybox-new/PromotedBuyBoxNew.vue');
const BuyBoxFilterNew = () => import('@/components/buybox-new/BuyBoxFilterNew.vue');
const RegularBuyBoxNew = () => import('@/components/buybox-new/RegularBuyBoxNew.vue');
const OfferReporter = () => import('@/components/buybox/OfferReporter.vue');

import { Vue, Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const Constant = namespace('constant');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'BuyBoxNew',
	components: {
		BuyBoxCountrySwitcher,
		PromotedBuyBoxNew,
		BuyBoxFilterNew,
		RegularBuyBoxNew,
		OfferReporter,
	},
	apollo: {
		offers: {
			query: GetBuyBoxOffersDocument,
			variables(): GetBuyBoxOffersQueryVariables {
				return this.queryVariables;
			},
			update(data: any) {
				if (data.node == null) return;
				if (
					data.node.__typename === 'Episode' ||
					data.node.__typename === 'Movie' ||
					data.node.__typename === 'Season' ||
					data.node.__typename === 'Show'
				) {
					const {
						buy,
						rent,
						fast,
						free,
						flatrate,
						promotedBundles,
						promotedOffers,
						offerCount,
						maxOfferUpdatedAt,
					} = data.node;
					const result: any = {
						stream: [],
						rent,
						buy,
						free,
						fast,
						cinema: [],
						promotedBundles,
						promotedOffers,
						offerCount,
						maxOfferUpdatedAt,
					};
					flatrate.forEach((offer: any) =>
						offer.monetizationType === MonetizationType.Cinema
							? result.cinema.push(offer)
							: result.stream.push(offer)
					);
					return result;
				}
			},
			// watchLoading(isLoading) {
			// 	if (!process.server) {
			// 		!this.buyboxLoaded && this.onBuyboxLoadingChange(isLoading);
			// 	}
			// },
		},
	},
	// setup({ title }) {
	// 	const { inPromotionCatalogue, promotionOffer, promotionCatalogue } = usePromotion({
	// 		title,
	// 		offers: () => {
	// 			const { free = [], stream } = useApolloData<any>('offers')();
	// 			return [...free, ...stream];
	// 		},
	// 	});
	// 	const { isArbitrating } = useArbitrage();
	// 	return { inPromotionCatalogue, promotionOffer, promotionCatalogue, isArbitrating };
	// },
})
export default class BuyBoxNew extends Vue {
	buyboxLoaded = false;
	offers = {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		promotedBundles: [],
		promotedOffers: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
	selectedFilter = BuyBoxFilterOptions.BEST;

	@Constant.Getter titleProviderCount: number;
	@Language.Getter language: string;
	@Language.Getter country: string;
	@User.Getter buyboxPresentationType: OfferPresentationType;

	@Prop() title: TitleDetailsNode;
	@Prop() titleId: string;
	@Prop() titleName: string | undefined;
	@Prop() titleObjectId: number;
	@Prop() titleObjectType: ObjectType;
	@Prop() titleScoring: Scoring;
	@Prop() originalReleaseYear: number | null;
	@Prop() sponsoredAd: SponsoredAdFragment;
	@Prop() additionalContexts: SnowplowContext[];

	// START AVOD
	@Prop({ default: false }) isAVODActive: boolean;
	// END AVOD

	get queryVariables() {
		return {
			nodeId: this.titleId,
			country: this.country,
			language: this.language,
			filterBuy: {
				monetizationTypes: [MonetizationType.Buy],
				bestOnly: true,
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
			},
			filterFlatrate: {
				monetizationTypes: [
					MonetizationType.Flatrate,
					MonetizationType.FlatrateAndBuy,
					MonetizationType.Ads,
					MonetizationType.Free,
					MonetizationType.Cinema,
				],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterRent: {
				monetizationTypes: [MonetizationType.Rent],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterFree: {
				monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
		};
	}

	get showPromotedBuyBox() {
		return (
			this.promotedBuyBoxOffers.promotedOffers?.length > 0 ||
			this.promotedBuyBoxOffers.promotedBundles?.length > 0
		);
	}

	get promotedBuyBoxOffers() {
		return {
			promotedOffers: this.offers?.promotedOffers,
			promotedBundles: this.offers?.promotedBundles,
		};
	}

	get buyBoxOffers() {
		return {
			offerCount: this.offers.offerCount,
			flatrate: this.offers.stream.filter((offer: any) => offer.monetizationType !== MonetizationType.Cinema),
			free: this.offers?.free,
			fast: this.offers?.fast,
			buy: this.offers?.buy,
			rent: this.offers?.rent,
		};
	}

	get presentationTypeGraphql() {
		// @ts-ignore
		const presentationTypeMap: Record<OfferPresentationType, PresentationType> = {
			[OfferPresentationType._4K]: PresentationType['4K'],
			[OfferPresentationType.HD]: PresentationType.Hd,
			[OfferPresentationType.SD]: PresentationType.Sd,
			[OfferPresentationType.DVD]: PresentationType.Dvd,
			[OfferPresentationType.BLURAY]: PresentationType.Bluray,
			[OfferPresentationType.CANVAS]: PresentationType.Canvas,
		};

		return presentationTypeMap[this.buyboxPresentationType as OfferPresentationType];
	}

	get buyBoxFilterOptions() {
		// @ts-ignore
		return Object.keys(this.buyBoxOffers).filter(key => this.buyBoxOffers?.[key]?.length > 0);
	}

	handleFilterClick(value: BuyBoxFilterOptions) {
		this.selectedFilter = value;
		TrackingHelper.trackEvent('presentationtype_change', { action: value ?? undefined }, this.additionalContexts);
	}

	get lastUpdated() {
		return this.offers.maxOfferUpdatedAt;
	}

	get timestampText() {
		const updated = timestamp(
			this.lastUpdated,
			{ year: 'numeric', month: 'long', day: 'numeric' },
			this.country,
			this.language
		);
		return this.$t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
			services_total: this.titleProviderCount,
			date: updated.date,
			time: updated.time,
		});
	}
}
